import axios from "axios";
import { isOnline } from "@/utils/connectivity";
import { hasFeature, hasAnyFeature } from "@/utils/features";
import {
  cachePosCatalog,
  cachePosSnapshot,
  cacheCategorias,
  getCachedPosCatalog,
  getCachedPosSnapshot,
  idbGetAll,
  idbDelete,
  toStorable,
  idbPut,
  syncOutboxCategorias,
} from "@/utils/posOffline/db";
import { fetchEstadoCajaActual } from "@/utils/cajaSesion";
import { listOutboxCobros, syncOutbox as syncOutboxSales } from "@/utils/posOffline/sync";
import { enrollDeviceIfNeeded } from "@/utils/posOffline/deviceEnrollment";

let warmInFlight = null;
let warmInFlightUsuario = null;

/** No dispara el GET si el módulo no está habilitado (evita 403 en consola). */
function getIfFeature(enabled, requestFn) {
  if (!enabled) return Promise.resolve(null);
  return requestFn();
}

const DEFAULT_CAJA = Object.freeze({
  requerida: true,
  abierta: false,
  cajaId: null,
  caja: null,
});

/** Caja activa: online refresca servidor; offline o error de red usa snapshot local. */
export async function resolveEstadoCaja(usuario) {
  const key = `caja:${usuario}`;
  if (isOnline()) {
    try {
      const st = await fetchEstadoCajaActual();
      const snapshot = {
        ...st,
        resumen: st.resumen || null,
        resumen_server: st.abierta ? st.resumen || null : null,
        cached_at: new Date().toISOString(),
      };
      await cachePosSnapshot(key, snapshot);
      return snapshot;
    } catch (_) {
      const cached = await getCachedPosSnapshot(key);
      if (cached) return cached;
    }
  }
  const cached = await getCachedPosSnapshot(key);
  return cached || { ...DEFAULT_CAJA };
}

export async function persistCajaEstado(usuario, estado) {
  if (!usuario || !estado) return;
  await cachePosSnapshot(`caja:${usuario}`, {
    ...estado,
    cached_at: new Date().toISOString(),
  });
}

export function sortProductosEstables(productos = []) {
  return [...(Array.isArray(productos) ? productos : [])].sort((a, b) => {
    const idA = String(a?.producto_id ?? a?.id ?? "");
    const idB = String(b?.producto_id ?? b?.id ?? "");
    const byId = idA.localeCompare(idB, "es", { numeric: true });
    if (byId !== 0) return byId;
    return String(a?.producto_nombre || "").localeCompare(
      String(b?.producto_nombre || ""),
      "es",
    );
  });
}

export function sameUsuarioOffline(a, b) {
  return String(a ?? "") === String(b ?? "");
}

export function mergeMovimientosStock(serverRows = [], localRows = []) {
  const merged = [];
  const seen = new Set();
  const addRow = (row) => {
    if (!row) return;
    const id = row._localOnly
      ? `local:${row.producto_id || "n/a"}:${row.creado_en || ""}:${row.motivo || ""}:${row.delta ?? ""}`
      : `srv:${row.id || row.movimiento_id || ""}:${row.producto_id || "n/a"}:${row.ref_pedido_id || ""}:${row.creado_en || ""}`;
    if (seen.has(id)) return;
    seen.add(id);
    merged.push(row);
  };
  for (const row of localRows) addRow(row);
  for (const row of serverRows) addRow(row);
  return merged.sort((a, b) =>
    String(b.creado_en || "").localeCompare(String(a.creado_en || "")),
  );
}

/** Precarga catálogo, categorías, negocio, clientes, ticket, cobros, stock, mesas y caja mientras hay conexión. */
export async function warmPosOfflineCache(usuario) {
  if (!usuario || !isOnline()) return;

  const canTickets = hasFeature("tickets");
  const canClientes = hasFeature("clientes");
  const canPedidos = hasAnyFeature("carrito_online", "pedido_mostrador", "pos");
  const canCaja = hasFeature("caja");
  const canStock = hasFeature("stock");
  const canMesas = hasFeature("mesas");

  const [
    pRes,
    catRes,
    tRes,
    nRes,
    cRes,
    pedRes,
    cajaSt,
    stockRes,
    mesasRes,
    zonasRes,
    vendRes,
  ] = await Promise.allSettled([
    axios.get("/productos", { params: { usuario } }),
    axios.get("/categorias", { params: { usuario } }),
    getIfFeature(canTickets, () => axios.get("/ticket-config")),
    axios.get(`/miNegocio?usuario=${encodeURIComponent(usuario)}`),
    getIfFeature(canClientes, () => axios.get("/clientes", { params: { usuario } })),
    getIfFeature(canPedidos, () =>
      axios.get("/pedidos", { params: { usuario, limit: 200 } }),
    ),
    getIfFeature(canCaja, () => fetchEstadoCajaActual()),
    getIfFeature(canStock, () =>
      axios.get("/stock/movimientos", { params: { usuario, limit: 300 } }),
    ),
    getIfFeature(canMesas, () => axios.get("/mesas")),
    getIfFeature(canMesas, () => axios.get("/mesas/zonas")),
    getIfFeature(canMesas, () => axios.get("/mesas/vendedores")),
  ]);

  if (pRes.status === "fulfilled" && Array.isArray(pRes.value?.data)) {
    await cachePosCatalog(
      `catalogo:${usuario}`,
      sortProductosEstables(pRes.value.data),
    );
  }
  if (catRes.status === "fulfilled" && Array.isArray(catRes.value?.data)) {
    await cacheCategorias(`categorias:${usuario}`, catRes.value.data);
  }
  if (tRes.status === "fulfilled" && tRes.value) {
    await cachePosSnapshot(`ticket:${usuario}`, tRes.value.data || {});
  }
  if (nRes.status === "fulfilled") {
    await cachePosSnapshot(`negocio:${usuario}`, nRes.value.data || {});
  }
  if (cRes.status === "fulfilled" && Array.isArray(cRes.value?.data)) {
    await cachePosSnapshot(`clientes:${usuario}`, cRes.value.data);
  }
  if (pedRes.status === "fulfilled" && Array.isArray(pedRes.value?.data)) {
    await cachePosSnapshot(`cobros:${usuario}`, pedRes.value.data);
  }
  if (cajaSt.status === "fulfilled" && cajaSt.value) {
    const st = cajaSt.value || {};
    await persistCajaEstado(usuario, {
      ...st,
      resumen: st.resumen || null,
      resumen_server: st.abierta ? st.resumen || null : null,
    });
  } else if (!canCaja) {
    await persistCajaEstado(usuario, { ...DEFAULT_CAJA, requerida: false, abierta: true });
  }
  if (stockRes.status === "fulfilled" && Array.isArray(stockRes.value?.data)) {
    const cacheKey = `stock_movimientos:${usuario}`;
    const cached = (await getCachedPosSnapshot(cacheKey)) || [];
    const localOnly = Array.isArray(cached)
      ? cached.filter((row) => row && row._localOnly)
      : [];
    await cachePosSnapshot(
      cacheKey,
      mergeMovimientosStock(stockRes.value.data, localOnly).slice(0, 300),
    );
  }
  if (mesasRes.status === "fulfilled" && Array.isArray(mesasRes.value?.data)) {
    await cachePosSnapshot(`mesas:${usuario}`, mesasRes.value.data);
  }
  if (zonasRes.status === "fulfilled" && zonasRes.value) {
    const zonas = (zonasRes.value.data && zonasRes.value.data.zonas) || [];
    await cachePosSnapshot(`mesas_zonas:${usuario}`, Array.isArray(zonas) ? zonas : []);
  }
  if (vendRes.status === "fulfilled" && Array.isArray(vendRes.value?.data)) {
    await cachePosSnapshot(`mesas_vendedores:${usuario}`, vendRes.value.data);
  }
  try {
    const { data } = await axios.get("/productos/ranking-ventas", { params: { usuario } });
    await cachePosSnapshot(`ventas_ranking:${usuario}`, (data && data.ranking) || {});
  } catch (_) {
    /* ranking opcional */
  }
  try {
    // Admin no es un negocio POS; no enrolar/renovar dispositivos en warm.
    if (usuario && String(usuario) !== "admin") {
      await enrollDeviceIfNeeded({ negocio: usuario });
    }
  } catch (_) {
    /* enrolar POS opcional si el plan no incluye dispositivos */
  }

  try {
    const { warmCatalogoMaestro } = await import("@/utils/catalogoAdopt");
    await warmCatalogoMaestro();
  } catch (_) {
    /* catálogo maestro opcional */
  }
}

/**
 * Precarga offline deduplicada (login / boot / reconnect).
 * Evita varias corridas en paralelo para el mismo usuario.
 */
export function ensureOfflineCacheWarm(usuario) {
  if (!usuario || !isOnline()) return Promise.resolve();
  if (warmInFlight && warmInFlightUsuario === String(usuario)) {
    return warmInFlight;
  }
  warmInFlightUsuario = String(usuario);
  warmInFlight = warmPosOfflineCache(usuario)
    .catch((err) => {
      console.warn("[offline] No se pudo precargar la caché:", err);
    })
    .finally(() => {
      if (warmInFlightUsuario === String(usuario)) {
        warmInFlight = null;
        warmInFlightUsuario = null;
      }
    });
  return warmInFlight;
}

export async function loadCachedPosBundle(usuario) {
  const [productos, ticketCfg, negocio, clientes, cobros, caja] =
    await Promise.all([
      getCachedPosCatalog(`catalogo:${usuario}`),
      getCachedPosSnapshot(`ticket:${usuario}`),
      getCachedPosSnapshot(`negocio:${usuario}`),
      getCachedPosSnapshot(`clientes:${usuario}`),
      getCachedPosSnapshot(`cobros:${usuario}`),
      getCachedPosSnapshot(`caja:${usuario}`),
    ]);
  return {
    productos: productos || [],
    ticketCfg: ticketCfg || {},
    negocio: negocio || {},
    clientes: clientes || [],
    cobros: Array.isArray(cobros) ? cobros : [],
    caja: caja || { ...DEFAULT_CAJA },
  };
}

/** Cobros del servidor cacheados + ventas locales pendientes/sincronizadas recientes. */
export async function resolveCobrosList(usuario) {
  const offlineRows = await listOutboxCobros({ includeSynced: false });
  if (isOnline() && hasAnyFeature("carrito_online", "pedido_mostrador", "pos")) {
    try {
      const { data } = await axios.get("/pedidos", {
        params: { usuario, limit: 200 },
      });
      const server = Array.isArray(data) ? data : [];
      await cachePosSnapshot(`cobros:${usuario}`, server);
      return mergeCobrosLists(server, offlineRows);
    } catch (_) {
      const cached = await getCachedPosSnapshot(`cobros:${usuario}`);
      const base = Array.isArray(cached) ? cached : [];
      return mergeCobrosLists(base, offlineRows);
    }
  }
  const cached = await getCachedPosSnapshot(`cobros:${usuario}`);
  const base = Array.isArray(cached) ? cached : [];
  return mergeCobrosLists(base, offlineRows);
}

function mergeCobrosLists(serverRows, offlineRows) {
  const seen = new Set();
  const merged = [];
  for (const row of offlineRows) {
    merged.push(row);
    if (row.client_mutation_id) seen.add(row.client_mutation_id);
  }
  for (const row of serverRows) {
    const mid = row.client_mutation_id;
    if (mid && seen.has(mid)) continue;
    merged.push(row);
  }
  merged.sort((a, b) =>
    String(b.creado_en || "").localeCompare(String(a.creado_en || "")),
  );
  return merged;
}

function buildPendingProductoEntry(usuario, producto, { modo = "crear" } = {}) {
  const productoId = producto?.producto_id ?? producto?.id ?? null;
  const idBase = productoId != null ? String(productoId) : `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entryId = `${modo}_${String(idBase).replace(/[^a-zA-Z0-9._-]/g, "_")}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  return toStorable({
    ...producto,
    producto_id: productoId != null ? String(productoId) : idBase,
    producto_nombre: producto?.nombre ?? producto?.producto_nombre ?? null,
    producto_precio: producto?.precio ?? producto?.producto_precio ?? null,
    producto_stock: producto?.stock ?? producto?.producto_stock ?? null,
    producto_categoria: producto?.categoria ?? producto?.producto_categoria ?? null,
    producto_codigo_barras:
      producto?.codigo_barras ?? producto?.producto_codigo_barras ?? null,
    producto_descripcion: producto?.descripcion ?? producto?.producto_descripcion ?? null,
    producto_imagen: producto?.imagen ?? producto?.producto_imagen ?? null,
    producto_imagenes:
      producto?.producto_imagenes ??
      producto?.imagenes ??
      null,
    producto_disponibilidad: producto?.producto_disponibilidad ?? 1,
    producto_en_pos: producto?.producto_en_pos ?? producto?.en_pos ?? 1,
    id: entryId,
    usuario,
    _pendingSync: true,
    _syncMode: modo,
    creado_en: new Date().toISOString(),
  });
}

function buildStockMovementEntry(usuario, producto, { delta = 0, motivo = "ajuste_manual", detalle = "" } = {}) {
  const productoId = producto?.producto_id ?? producto?.id ?? null;
  const stock = producto?.producto_stock ?? producto?.stock ?? null;
  return toStorable({
    producto_id: productoId != null ? String(productoId) : null,
    producto_nombre: producto?.producto_nombre ?? producto?.nombre ?? null,
    delta: Number(delta) || 0,
    stock_resultante: stock != null ? Number(stock) : null,
    motivo: motivo || "ajuste_manual",
    detalle: detalle || "",
    usuario_negocio: usuario,
    creado_en: new Date().toISOString(),
    _localOnly: true,
  });
}

export async function guardarMovimientoStockOffline(usuario, producto, { delta = 0, motivo = "ajuste_manual", detalle = "" } = {}) {
  if (!usuario) return null;
  const cacheKey = `stock_movimientos:${usuario}`;
  const existing = (await getCachedPosSnapshot(cacheKey)) || [];
  const rows = Array.isArray(existing) ? existing : [];
  const entry = buildStockMovementEntry(usuario, producto, { delta, motivo, detalle });
  rows.unshift(entry);
  await cachePosSnapshot(cacheKey, rows.slice(0, 300));
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event("stock:movements-updated"));
  }
  return entry;
}

/**
 * Ajusta stock en el catálogo local sin encolar PUT /modificarProducto.
 * Usado tras ventas POS/mesa (el servidor descuenta al sincronizar el pedido).
 * @param {Array<{ producto_id: any, qty?: number, delta?: number, producto_nombre?: string }>} cambios
 *   qty positiva = unidades vendidas (resta stock); delta = cambio neto de stock.
 */
export async function aplicarStockLocalCatalogo(
  usuario,
  cambios = [],
  { motivo = "venta_pos", detalle = "", registrarMovimiento = true } = {},
) {
  if (!usuario || !Array.isArray(cambios) || !cambios.length) {
    return (await getCachedPosCatalog(`catalogo:${usuario}`)) || [];
  }

  const cacheKey = `catalogo:${usuario}`;
  const catalogo = [...((await getCachedPosCatalog(cacheKey)) || [])];

  for (const cambio of cambios) {
    const productoId = cambio?.producto_id ?? cambio?.id;
    if (productoId == null) continue;
    const idx = catalogo.findIndex(
      (p) => String(p.producto_id ?? p.id) === String(productoId),
    );
    if (idx < 0) continue;

    const actual = catalogo[idx];
    const stockActual = Number(actual.producto_stock ?? actual.stock);
    if (!Number.isFinite(stockActual) || stockActual === -1) continue;

    let deltaStock = Number(cambio.delta);
    if (!Number.isFinite(deltaStock)) {
      const qty = Number(cambio.qty || 0);
      deltaStock = -Math.abs(qty);
    }
    if (!deltaStock) continue;

    const stockNuevo = stockActual + deltaStock;
    catalogo[idx] = {
      ...actual,
      producto_stock: stockNuevo,
      stock: stockNuevo,
    };

    if (registrarMovimiento) {
      await guardarMovimientoStockOffline(
        usuario,
        {
          ...catalogo[idx],
          producto_nombre:
            cambio.producto_nombre ||
            actual.producto_nombre ||
            actual.nombre ||
            null,
        },
        {
          delta: deltaStock,
          motivo,
          detalle:
            detalle ||
            (motivo === "venta_pos"
              ? "Venta POS (local)"
              : motivo === "pedido_mesa"
                ? "Pedido mesa (local)"
                : ""),
        },
      );
    }
  }

  const sorted = sortProductosEstables(catalogo);
  await cachePosCatalog(cacheKey, sorted);
  return sorted;
}

/** Guarda un producto nuevo en el catálogo local y lo agrega a la cola de sincronización. */
export async function guardarProductoOffline(usuario, nuevoProducto) {
  const productoConTempId = buildPendingProductoEntry(usuario, nuevoProducto, { modo: "crear" });
  await idbPut("outbox_productos", productoConTempId);
  return productoConTempId;
}

/** Guarda cambios sobre un producto existente (editar, stock, precio o borrar) para sincronizarlos luego. */
export async function guardarProductoCambioOffline(usuario, productoModificado, { modo = "editar" } = {}) {
  const cacheKey = `catalogo:${usuario}`;
  const catalogoCached = (await getCachedPosCatalog(cacheKey)) || [];
  const productoId = productoModificado?.producto_id ?? productoModificado?.id ?? null;

  if (modo === "delete") {
    const productoConCambio = buildPendingProductoEntry(usuario, productoModificado, { modo });
    await idbPut("outbox_productos", productoConCambio);
    const filtrado = catalogoCached.filter(
      (p) => String(p.producto_id ?? p.id) !== String(productoId),
    );
    await cachePosCatalog(cacheKey, sortProductosEstables(filtrado));
    return productoConCambio;
  }

  const index = catalogoCached.findIndex(
    (p) => String(p.producto_id ?? p.id) === String(productoId),
  );

  let stockDelta = null;
  if (modo === "stock") {
    const stockAnterior = index >= 0
      ? Number(catalogoCached[index]?.producto_stock ?? catalogoCached[index]?.stock ?? 0)
      : Number(productoModificado?.producto_stock ?? productoModificado?.stock ?? 0);
    const stockNuevo = Number(
      productoModificado?.producto_stock ?? productoModificado?.stock ?? stockAnterior,
    );
    stockDelta = Number.isFinite(stockNuevo) && Number.isFinite(stockAnterior)
      ? stockNuevo - stockAnterior
      : 0;
  }

  const productoConCambio = buildPendingProductoEntry(usuario, productoModificado, { modo });
  if (modo === "stock" && stockDelta != null) {
    productoConCambio._stockDelta = stockDelta;
  }
  await idbPut("outbox_productos", productoConCambio);

  if (index >= 0) {
    const prev = catalogoCached[index];
    catalogoCached[index] = {
      ...prev,
      ...productoModificado,
      producto_id: prev.producto_id ?? productoId,
      _pendingSync: true,
      _syncMode: modo,
    };
  } else {
    catalogoCached.push({
      ...productoModificado,
      producto_id: productoId,
      _pendingSync: true,
      _syncMode: modo,
    });
  }

  if (modo === "stock" && stockDelta) {
    const stockNuevo = Number(
      productoModificado?.producto_stock ?? productoModificado?.stock ?? 0,
    );
    await guardarMovimientoStockOffline(usuario, {
      ...productoModificado,
      producto_stock: stockNuevo,
      stock: stockNuevo,
    }, {
      delta: stockDelta,
      motivo: "ajuste_manual",
      detalle: "Ajuste offline",
    });
  }

  await cachePosCatalog(cacheKey, sortProductosEstables(catalogoCached));
  return productoConCambio;
}

export async function guardarProductoModificadoOffline(usuario, productoModificado) {
  return guardarProductoCambioOffline(usuario, productoModificado, { modo: "editar" });
}

export function mergeProductosCatalogo(productosBase = [], productosExtra = []) {
  const merged = [
    ...(Array.isArray(productosBase) ? productosBase : []),
    ...(Array.isArray(productosExtra) ? productosExtra : []),
  ];
  const byId = new Map();

  for (const producto of merged) {
    if (!producto || producto._syncMode === "delete") continue;
    const key =
      producto.producto_id != null ? String(producto.producto_id) : null;
    if (!key) continue;
    const existing = byId.get(key);
    if (!existing || producto._pendingSync) {
      byId.set(key, {
        ...producto,
        producto_id: key,
      });
    }
  }

  return sortProductosEstables([...byId.values()]);
}

export function deduplicarProductos(productos = []) {
  if (!Array.isArray(productos)) return [];
  const byId = new Map();
  for (const producto of productos) {
    const key =
      producto?.producto_id != null ? String(producto.producto_id) : null;
    if (!key) continue;
    if (!byId.has(key)) {
      byId.set(key, { ...producto, producto_id: key });
    }
  }
  return sortProductosEstables([...byId.values()]);
}

export async function obtenerProductosCatalogo(usuario) {
  const cacheKey = `catalogo:${usuario}`;

  const catalogoCached = ((await getCachedPosCatalog(cacheKey)) || []).filter(
    (p) => p && p._syncMode !== "delete",
  );

  const outboxProductos = (await idbGetAll("outbox_productos")) || [];
  const misPendientes = outboxProductos.filter(
    (p) => sameUsuarioOffline(p.usuario, usuario) && p._syncMode !== "delete",
  );
  const deletes = outboxProductos.filter(
    (p) => sameUsuarioOffline(p.usuario, usuario) && p._syncMode === "delete",
  );
  const deletedIds = new Set(
    deletes.map((p) => String(p.producto_id ?? p.id)).filter(Boolean),
  );

  const merged = mergeProductosCatalogo(catalogoCached, misPendientes);
  return merged.filter((p) => !deletedIds.has(String(p.producto_id ?? p.id)));
}

export async function syncPendingOfflineWork(usuario) {
  if (!usuario || !isOnline()) {
    return {
      syncedSales: 0,
      syncedProducts: 0,
      syncedCategories: 0,
      syncedMesas: 0,
      syncedCajas: 0,
      skipped: true,
    };
  }

  let syncOutboxMesas = async () => ({ syncedCount: 0 });
  let syncOutboxCajasPreSales = async () => ({ syncedCount: 0 });
  let syncOutboxCajasPostSales = async () => ({ syncedCount: 0 });
  try {
    ({ syncOutboxMesas } = await import("@/utils/posOffline/mesasSync"));
  } catch (_) {
    /* módulo opcional durante bootstrap */
  }
  try {
    ({
      syncOutboxCajasPreSales,
      syncOutboxCajasPostSales,
    } = await import("@/utils/posOffline/cajaSync"));
  } catch (_) {
    /* módulo opcional */
  }

  // 1) Abrir caja + movimientos locales antes de empujar ventas.
  const cajaPre = await Promise.resolve(syncOutboxCajasPreSales(usuario)).catch(() => ({
    syncedCount: 0,
  }));

  const [sales, products, categories, mesas] = await Promise.allSettled([
    syncOutboxSales(),
    syncOutboxProductos(usuario),
    syncOutboxCategorias(usuario),
    syncOutboxMesas(usuario),
  ]);

  // 2) Cerrar caja después de sincronizar ventas.
  const cajaPost = await Promise.resolve(syncOutboxCajasPostSales(usuario)).catch(() => ({
    syncedCount: 0,
  }));

  return {
    syncedSales: sales.status === "fulfilled" ? Number(sales.value?.synced || 0) : 0,
    syncedProducts: products.status === "fulfilled" ? Number(products.value?.syncedCount || 0) : 0,
    syncedCategories: categories.status === "fulfilled" ? Number(categories.value?.syncedCount || 0) : 0,
    syncedMesas: mesas.status === "fulfilled" ? Number(mesas.value?.syncedCount || 0) : 0,
    syncedCajas:
      Number(cajaPre?.syncedCount || 0) + Number(cajaPost?.syncedCount || 0),
    skipped: false,
  };
}

/** Procesa y envía al backend los productos creados/editados offline */
export async function syncOutboxProductos(usuario) {
  if (!isOnline()) return { syncedCount: 0 };

  const queue = await idbGetAll("outbox_productos");
  const mine = (queue || []).filter((p) => sameUsuarioOffline(p.usuario, usuario));
  if (!mine.length) return { syncedCount: 0 };

  let syncedCount = 0;

  for (const prod of mine) {
    try {
      const modoRaw = prod._syncMode || "crear";
      const productoId = prod.producto_id ?? null;
      const payload = { ...prod };
      delete payload.id;
      delete payload._pendingSync;
      delete payload.creado_en;
      delete payload._syncMode;
      delete payload._stockDelta;
      delete payload.usuario;

      if (modoRaw === "delete") {
        if (productoId) {
          await axios.delete(`/eliminarProducto?id=${encodeURIComponent(productoId)}`);
        }
      } else if (modoRaw === "stock") {
        const delta = Number(prod._stockDelta);
        if (productoId && Number.isFinite(delta) && delta !== 0) {
          await axios.post("/stock/ajuste", {
            usuario,
            producto_id: productoId,
            delta,
            motivo: "ajuste_manual",
            detalle: "Ajuste offline",
          });
        } else if (productoId) {
          await axios.put("/modificarProducto", { producto: payload });
        }
      } else if (modoRaw === "editar" || modoRaw === "precio") {
        await axios.put("/modificarProducto", { producto: payload });
      } else {
        await axios.post("/nuevoProducto", { producto: payload });
      }

      await idbDelete("outbox_productos", prod.id);
      syncedCount++;
    } catch (error) {
      console.error("Error sincronizando producto offline:", prod, error);
    }
  }

  if (syncedCount > 0) {
    try {
      const { data } = await axios.get("/productos", { params: { usuario } });
      if (Array.isArray(data)) {
        await cachePosCatalog(`catalogo:${usuario}`, sortProductosEstables(data));
      }
      if (hasFeature("stock")) {
        const stockRes = await axios.get("/stock/movimientos", {
          params: { usuario, limit: 300 },
        });
        if (Array.isArray(stockRes.data)) {
          await cachePosSnapshot(
            `stock_movimientos:${usuario}`,
            mergeMovimientosStock(stockRes.data, []).slice(0, 300),
          );
          if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
            window.dispatchEvent(new Event("stock:movements-updated"));
          }
        }
      }
    } catch (e) {
      console.error("No se pudo refrescar la caché tras sincronizar:", e);
    }
  }

  return { syncedCount };
}